// Centralised case study data — generic, no client names disclosed.
// Organised by vertical + primary service. Full content for /case-studies and /case-studies/:slug.

export const CASE_STUDIES_FULL = [

  // ─── FINTECH ─────────────────────────────────────────────────────────────

  {
    slug: "neobank-app-aso-growth",
    category: "FinTech",
    appName: "Neobank App",
    title: "+310% organic installs for a neobank app",
    metric: "+310%",
    metricLabel: "organic installs",
    summary:
      "Full ASO rebuild — keyword architecture, metadata, screenshots and review strategy — tripled organic installs for a neobank app competing against tier-1 incumbents across USA, UK and Australia.",
    market: "USA · UK · Australia",
    platform: "both",
    services: ["ASO", "Ratings & Reviews", "Analytics"],
    duration: "90 days",
    challenge:
      "A fast-growing neobank had a strong product but was invisible in App Store and Google Play searches. Keyword coverage was thin, ratings sat at 3.2★, and screenshots led with features instead of outcomes.",
    approach: [
      "Full keyword audit — mapped 400+ intent-bearing queries across personal finance, money transfer, budgeting and account opening.",
      "Rewrote title, subtitle and description for App Store and Google Play with primary + secondary keyword clustering.",
      "New screenshot set leading with outcomes (spend less, save more, open in 3 minutes) instead of UI features.",
      "Review response operations: 24-hour SLA on 1–3★ reviews, templated escalation flows, rating-prompt timing A/B test.",
      "Monthly metadata iteration cadence anchored to ranking velocity data.",
    ],
    results: [
      { value: "+310%", label: "organic installs" },
      { value: "+180%", label: "keyword rankings (top-20)" },
      { value: "4.6★", label: "avg rating (was 3.2★)" },
      { value: "−28%", label: "paid acquisition dependency" },
    ],
    quote: {
      text: "We stopped relying on paid to drive volume. AppFillip's ASO work made organic our #1 install channel in under three months.",
      author: "Growth Lead, Neobank App",
    },
  },

  {
    slug: "investment-app-global-aso",
    category: "FinTech",
    appName: "Investment App",
    title: "+265% App Store visibility for an investment app",
    metric: "+265%",
    metricLabel: "App Store visibility",
    summary:
      "ASO, Creative Production and Analytics boosted a retail investment app's visibility by 265% across UK, Germany, France and Spain within one quarter.",
    market: "UK · Germany · France · Spain",
    platform: "ios",
    services: ["ASO", "Creative Production", "Analytics"],
    duration: "3 months",
    challenge:
      "A retail investment app was live in four European markets but ranked below page 3 for every high-intent query (stocks, ETFs, ISA). Creatives were generic and CVR was well below category average.",
    approach: [
      "Per-market keyword strategy with locale-specific query research (ISA in UK, Sparplan in Germany, PEA in France).",
      "Produced localised screenshot sets per market — different regulatory messaging, different benefit hierarchy.",
      "Custom Product Pages (iOS) A/B-tested for acquisition source: organic vs. paid social audiences.",
      "Weekly analytics review loop: keyword delta → metadata iteration → CVR tracking.",
    ],
    results: [
      { value: "4×", label: "App Store visibility" },
      { value: "+95%", label: "store conversion rate" },
      { value: "Top 5", label: "Finance category rank in 3 markets" },
      { value: "+62%", label: "organic installs MoM" },
    ],
    quote: {
      text: "Market-specific ASO is obvious in theory. AppFillip is the first agency that actually executed it — per country, per language, per regulatory nuance.",
      author: "Product Marketing, Investment App",
    },
  },

  {
    slug: "digital-wallet-app-store-conversion",
    category: "FinTech",
    appName: "Digital Wallet App",
    title: "+125% store conversion for a digital wallet app",
    metric: "+125%",
    metricLabel: "store conversion",
    summary:
      "A trust-and-utility conversion program - combining payment-intent positioning, onboarding clarity, credibility cues and transaction-focused creative testing - helped a digital wallet app achieve +125% store conversion across India, Singapore and the UAE.",
    market: "India · Singapore · UAE",
    platform: "both",
    services: [
      "Conversion Optimization",
      "Creative Strategy",
      "ASO",
      "A/B Testing",
      "Funnel Analytics",
    ],
    duration: "110 days",
    challenge:
      "The wallet made it easy to send money, pay bills, manage everyday transactions and track spending, but prospective users were cautious before installing. The existing store page focused heavily on functionality while leaving important questions unanswered: Is my money secure? How quickly can I get started? What can I actually pay for? Why should I use this instead of another wallet? The product didn't need more features on the page. It needed to remove uncertainty between seeing the app and trusting it enough to install.",
    approach: [
      "Mapped the questions users ask before installing: user concerns were grouped around security, setup time, payment acceptance, transfer speed, fees and account control.",
      "Reordered the story around the first transaction: the gallery followed a practical sequence from setup to adding money, paying or transferring, tracking and staying in control.",
      "Put credibility where doubt was highest: security and trust cues were introduced before uncertainty could cause abandonment.",
      "Tested convenience against financial control: creative tests measured faster payments against money visibility and control instead of assuming one universal install motivation.",
      "Localized the payment story by market: store messaging was adjusted around locally relevant payment habits across India, Singapore and the UAE.",
      "Optimized for activation, not just installs: store experiments were evaluated against account activation and first-transaction signals.",
    ],
    results: [
      { value: "+125%", label: "store conversion" },
      { value: "+88%", label: "install-to-activation rate" },
      { value: "+64%", label: "first-transaction completion" },
      { value: "-27%", label: "cost per activated user" },
    ],
    quote: {
      text: "For a financial product, getting the install wasn't enough. We needed people to arrive already understanding why they could trust the app and what they could do with it. That changed both conversion and activation.",
      author: "Head of Mobile Growth, Digital Wallet App",
    },
  },

  {
    slug: "payment-app-india-ua",
    category: "FinTech",
    appName: "Payment App",
    title: "+178% installs for a payment app across India and SEA",
    metric: "+178%",
    metricLabel: "install growth",
    summary:
      "Paid Search, Paid Social and ASO grew a digital-payment app's installs by 178% across India, Singapore and UAE with a unified multi-market paid architecture.",
    market: "India · Singapore · UAE",
    platform: "both",
    services: ["User Acquisition", "Paid Social", "ASO"],
    duration: "6 months",
    challenge:
      "A payment app had product-market fit in India but CPIs were rising and expansion into Singapore and UAE stalled. Single-market campaigns were bleeding budget without quality.",
    approach: [
      "Built per-market campaign structures — India on volume + vernacular creative, Singapore/UAE on premium intent targeting.",
      "Paid Social with use-case creative: peer transfers, bill splits, merchant QR payments as separate funnels.",
      "ASO localisation for English (SG/UAE) and Hindi/Tamil (India) keyword universes.",
      "MMP attribution setup to score install quality by first-transaction rate, not just volume.",
    ],
    results: [
      { value: "+178%", label: "install growth" },
      { value: "−38%", label: "blended CPI" },
      { value: "+55%", label: "first-transaction rate" },
      { value: "3", label: "new markets activated" },
    ],
    quote: {
      text: "Same budget, three markets, 178% more quality installs. AppFillip's multi-market architecture was the missing piece.",
      author: "Marketing Manager, Payment App",
    },
  },

  {
    slug: "crypto-trading-app-keyword-rankings",
    category: "FinTech",
    appName: "Crypto Trading App",
    title: "+240% Top-20 keyword rankings for a crypto trading app",
    metric: "+240%",
    metricLabel: "top-20 keyword rankings",
    summary:
      "A competition-led ASO program - combining crypto search segmentation, trust-first positioning, market-cycle keyword planning and trading-focused store creatives - helped a crypto trading app achieve +240% Top-20 keyword rankings across the USA, UK and Australia.",
    market: "USA · UK · Australia",
    platform: "both",
    services: [
      "ASO",
      "Competitor Intelligence",
      "Search Strategy",
      "Creative Positioning",
      "Market Analytics",
    ],
    duration: "120 days",
    challenge:
      "The app gave users access to cryptocurrency markets, price tracking, portfolio monitoring and trading tools, but its store visibility was concentrated around the brand and a narrow set of broad crypto terms. Search behaviour changed rapidly with market sentiment. Users moved between searches for Bitcoin, altcoins, crypto prices, portfolio tracking and trading tools, while major exchanges occupied many of the obvious category keywords. The app needed a way to compete without depending on a handful of expensive, overcrowded search terms.",
    approach: [
      "Split crypto search into distinct trader intents: demand was divided across buying crypto, tracking prices, monitoring portfolios, researching coins, following Bitcoin and accessing trading tools.",
      "Identified keywords the largest exchanges did not fully own: competitive coverage was examined for underserved combinations of asset, action and trading intent.",
      "Made trust part of the first impression: security, transparency and account-control messaging moved closer to the opening creatives.",
      "Separated trading from tracking: creative paths distinguished between users who wanted to monitor the market, follow a portfolio or take action on an opportunity.",
      "Adapted search priorities to market momentum: keyword priorities were reviewed against shifts in search behaviour and asset interest.",
      "Graduated from niche terms into competitive territory: early momentum was built through specific search opportunities, then expanded toward broader crypto terms.",
    ],
    results: [
      { value: "+240%", label: "top-20 keyword rankings" },
      { value: "+134%", label: "non-branded search impressions" },
      { value: "+79%", label: "search-driven installs" },
      { value: "+46%", label: "store conversion from crypto searches" },
    ],
    quote: {
      text: "The category changes too quickly for a static keyword strategy. Once we started treating trading, tracking and individual asset searches differently, we found growth opportunities outside the terms every major exchange was fighting for.",
      author: "Growth Director, Crypto Trading App",
    },
  },

  {
    slug: "crypto-app-aso-analytics",
    category: "FinTech",
    appName: "Crypto Trading App",
    title: "+240% keyword rankings for a crypto trading app",
    metric: "+240%",
    metricLabel: "top-20 keyword rankings",
    summary:
      "ASO and Analytics for a crypto trading app across USA, UK, Germany and Australia — keyword rankings nearly tripled and store CVR jumped 70% in 90 days.",
    market: "USA · UK · Germany · Australia",
    platform: "both",
    services: ["ASO", "Analytics", "Ratings & Reviews"],
    duration: "90 days",
    challenge:
      "A crypto trading app had been live for 18 months with minimal ASO investment. Organic visibility was dwarfed by category leaders, reviews were scattered and there was no iterative metadata process.",
    approach: [
      "Keyword gap analysis vs. top-5 competitors — identified 60+ uncontested mid-tail queries worth targeting.",
      "Metadata rewrite anchored to 'buy crypto', 'crypto wallet', 'Bitcoin app' and 10 mid-tail clusters.",
      "Google Play long-description SEO refresh with natural keyword density and feature-benefit hierarchy.",
      "Rating-prompt A/B test: contextual trigger (post-profitable-trade) vs generic timer — 2× prompt acceptance.",
    ],
    results: [
      { value: "+240%", label: "top-20 keyword rankings" },
      { value: "+70%", label: "store conversion rate" },
      { value: "4.5★", label: "avg rating (was 3.9★)" },
      { value: "+88%", label: "organic installs" },
    ],
    quote: {
      text: "We'd been relying on brand searches. AppFillip opened up a whole new layer of intent we were completely missing.",
      author: "Head of Growth, Crypto Trading App",
    },
  },

  {
    slug: "insurance-app-gtm",
    category: "FinTech",
    appName: "Insurance App",
    title: "Successful GTM launch for an insurance app in 3 markets",
    metric: "3",
    metricLabel: "markets launched in 60 days",
    summary:
      "Full GTM strategy — positioning, ASO, paid launch and PR — shipped a digital insurance app to USA, UK and Germany simultaneously, hitting 50K installs in the first 60 days.",
    market: "USA · UK · Germany",
    platform: "both",
    services: ["GTM Strategy", "ASO", "Paid Search", "Paid Social"],
    duration: "60-day launch + retainer",
    challenge:
      "A digital insurance startup was preparing to launch in three markets at once with no prior app marketing infrastructure, no ASO baseline and a tight budget window.",
    approach: [
      "60-day launch playbook: positioning workshop → ASO build → paid account structure → creative production → soft launch → scale.",
      "Per-market keyword and metadata builds — different regulatory language per jurisdiction.",
      "Apple Search Ads and Google UAC launch campaigns with tight CPA targets from day one.",
      "Post-install lifecycle: onboarding email series, push sequences and first-quote nudge.",
    ],
    results: [
      { value: "50K", label: "installs in first 60 days" },
      { value: "3", label: "markets launched simultaneously" },
      { value: "Top 10", label: "Finance category rank in UK" },
      { value: "+42%", label: "quote-completion rate vs industry avg" },
    ],
    quote: {
      text: "Launching in three markets at once felt impossible. AppFillip turned it into a 60-day sprint and we hit every milestone.",
      author: "CEO, Insurance App",
    },
  },

  // ─── GAMING ──────────────────────────────────────────────────────────────

  {
    slug: "personal-finance-app-aso-growth",
    category: "FinTech",
    appName: "Personal Finance App",
    title: "+218% organic installs for a personal finance app",
    metric: "+218%",
    metricLabel: "organic installs",
    summary:
      "Full ASO optimization - keyword architecture, metadata restructuring, screenshot optimization and conversion testing - helped a personal finance app achieve +218% organic installs while competing against established budgeting and money-management apps across the USA, Canada and UK.",
    market: "USA · Canada · UK",
    platform: "both",
    services: [
      "ASO",
      "Analytics",
      "Creative Optimization",
      "Keyword Research",
      "Conversion Optimization",
    ],
    duration: "120 days",
    challenge:
      "A growing personal finance app had strong budgeting, expense-tracking and savings features, but struggled to generate meaningful discovery beyond branded searches. High-intent keyword coverage was limited, generic finance terms were dominated by larger competitors, and the store screenshots focused heavily on individual features. Meanwhile, weak positioning across the listing was limiting both organic acquisition and store-page conversion.",
    approach: [
      "Built a high-intent keyword architecture: completed a full keyword audit and mapped 500+ intent-bearing queries across budgeting, expense tracking, spending management, savings and personal money management.",
      "Restructured store metadata: reworked the title, subtitle, short description and long description for the App Store and Google Play, using primary and secondary keyword clusters based on relevance, competition and search intent.",
      "Repositioned screenshots around financial outcomes: created a new screenshot set leading with control your spending, understand where your money goes and build better saving habits instead of presenting individual product features without context.",
      "Optimized store-page conversion: implemented creative testing across screenshot hooks, messaging hierarchy and value propositions to identify which combinations generated stronger engagement and install conversion.",
      "Introduced market-specific optimization: established separate optimization priorities across the USA, Canada and UK based on keyword performance, competitive density and differences in user search behaviour.",
      "Established continuous ASO iteration: introduced a monthly metadata and creative optimization cadence anchored to keyword rankings, impressions, organic installs and store conversion data.",
    ],
    results: [
      { value: "+218%", label: "organic installs" },
      { value: "+156%", label: "keyword rankings (top-20)" },
      { value: "+47%", label: "store conversion rate" },
      { value: "-31%", label: "paid acquisition dependency" },
    ],
    quote: {
      text: "We had the product people needed, but our store presence wasn't communicating it. The new ASO strategy helped us reach users searching for exactly what we offered and convert more of them organically.",
      author: "Growth Lead, Personal Finance App",
    },
  },

  {
    slug: "mobile-gaming-app-creative-install-growth",
    category: "Gaming",
    appName: "Mobile Gaming App",
    title: "+145% install growth for a mobile gaming app",
    metric: "+145%",
    metricLabel: "install growth",
    summary:
      "A creative-led store growth campaign - combining player-motivation research, icon experimentation, gameplay-first screenshots and preview-video testing - helped a mobile game achieve +145% install growth across the USA, Germany and Brazil.",
    market: "USA · Germany · Brazil",
    platform: "both",
    services: [
      "Creative Optimization",
      "ASO",
      "A/B Testing",
      "Store Video Strategy",
      "User Acquisition",
    ],
    duration: "90 days",
    challenge:
      "Players who downloaded the game showed healthy engagement, but the listing wasn't convincing enough new visitors to give it a try. The icon blended into a crowded category, screenshots relied on static UI, and the preview video took too long to reveal the most satisfying gameplay moments. Paid campaigns were successfully bringing people to the store, but too many of those visits ended without an install. For a game where users make decisions in seconds, the store page wasn't creating enough curiosity, excitement or urgency.",
    approach: [
      "Found the moments players actually cared about: gameplay behaviour and player feedback were studied to identify emotional moments around progression, rewards, competition, close wins and satisfying gameplay.",
      "Put the icon through a tournament: multiple icon directions were developed around character focus, action, progression and reward, then tested until a clear winner emerged.",
      "Turned screenshots into mini gameplay stories: static feature explanations were replaced with visual scenarios showing challenge, action, reward, rivalry and progression.",
      "Moved the payoff to the start of the preview: the replacement preview opened directly on high-energy gameplay and showed satisfying interactions early.",
      "Built different creative directions for different markets: variants were tested around competition, characters, rewards and progression across the USA, Germany and Brazil.",
      "Connected ad promises to store creative: winning paid-ad concepts were carried into the product page so the store experience reinforced the same expectation.",
    ],
    results: [
      { value: "+145%", label: "install growth" },
      { value: "+67%", label: "product page conversion" },
      { value: "+92%", label: "browse-driven installs" },
      { value: "-29%", label: "cost per install" },
    ],
    quote: {
      text: "We had been optimizing ads aggressively, but the biggest leak was happening after the click. Once the store page started selling the actual feeling of playing the game, the same traffic produced far more installs.",
      author: "Performance Marketing Lead, Mobile Gaming App",
    },
  },

  {
    slug: "casual-game-global-expansion",
    category: "Gaming",
    appName: "Casual Game",
    title: "+215% installs for a casual game across 5 global markets",
    metric: "+215%",
    metricLabel: "install growth across global markets",
    summary:
      "Paid Social, Creative Production and International Expansion drove 215% more installs for a casual game across USA, UK, Germany, Brazil and Japan with localised creative per market.",
    market: "USA · UK · Germany · Brazil · Japan",
    platform: "both",
    services: ["Paid Social", "Creative Production", "International Expansion", "ASO"],
    duration: "6 months",
    challenge:
      "A casual game had strong performance in its home market but failed to scale globally. Generic one-size-fits-all campaigns underperformed; localised creative was non-existent.",
    approach: [
      "Per-market campaign architecture: separate Paid Social structures for each of the 5 markets.",
      "Native-language creative produced for German, Portuguese (BR) and Japanese markets — not translations, new concepts.",
      "ASO localisation per market: App Store subtitle and screenshots adapted to local gaming culture.",
      "Creative testing velocity: 8+ concepts per market per month with winner-scaling methodology.",
    ],
    results: [
      { value: "+215%", label: "global install growth" },
      { value: "5", label: "new markets activated" },
      { value: "−35%", label: "blended CPI vs single-market baseline" },
      { value: "+48%", label: "store CVR in localized markets" },
    ],
    quote: {
      text: "We had fans in five markets and couldn't reach them. AppFillip's localised creative closed the gap — installs more than tripled.",
      author: "UA Manager, Casual Game",
    },
  },

  {
    slug: "hypercasual-game-creative",
    category: "Gaming",
    appName: "Hyper-Casual Game",
    title: "+420% ROAS for a hyper-casual game via creative optimisation",
    metric: "+420%",
    metricLabel: "ROAS improvement",
    summary:
      "Creative Production and Paid Social overhaul quadrupled ROAS for a hyper-casual game — cutting CPI by 55% while maintaining install volume at scale across 15 global markets.",
    market: "Global · 15 markets",
    platform: "both",
    services: ["Creative Production", "Paid Social", "Analytics"],
    duration: "4 months",
    challenge:
      "A hyper-casual publisher was scaling spend but ROAS was deteriorating. Creative fatigue was severe — the same 3 video concepts had been running for 6 months. CPIs were 2× industry benchmark.",
    approach: [
      "Creative audit: identified top-performing hooks, mechanics and CTAs from historical data across all campaigns.",
      "Built a systematic creative factory: 20+ new concepts monthly across playable, UGC, gameplay-loop and meme formats.",
      "A/B testing framework with statistical significance gates — no concept scaled until it beat the control.",
      "Dynamic creative optimisation on Meta and Google to serve winning creative combinations per audience segment.",
    ],
    results: [
      { value: "+420%", label: "ROAS improvement" },
      { value: "−55%", label: "CPI" },
      { value: "20+", label: "new creatives tested monthly" },
      { value: "15", label: "markets maintained at scale" },
    ],
    quote: {
      text: "We were burning budget on zombie creatives. AppFillip rebuilt our creative engine from scratch — ROAS quadrupled without cutting volume.",
      author: "Performance Lead, Hyper-Casual Game",
    },
  },

  {
    slug: "rpg-game-korea-japan",
    category: "Gaming",
    appName: "RPG Game",
    title: "Top 10 chart position for an RPG in South Korea and Japan",
    metric: "Top 10",
    metricLabel: "chart rank in KR + JP",
    summary:
      "ASO, Influencer Marketing and Creative Production pushed an RPG to Top 10 in both South Korea and Japan App Store charts within 45 days of regional launch.",
    market: "South Korea · Japan · Taiwan",
    platform: "ios",
    services: ["ASO", "Influencer Marketing", "Creative Production", "International Expansion"],
    duration: "45-day launch",
    challenge:
      "An established RPG launching in East Asia faced intense competition from local titles. Generic English creative bombed; the team had no local influencer network or native ASO capability.",
    approach: [
      "Korean and Japanese ASO builds: genre-specific keywords, localised screenshots using native UI conventions.",
      "Influencer seeding: 20 mid-tier gaming streamers in Korea, 15 in Japan — gameplay reveal + launch-day content.",
      "Localised creatives — anime art direction for Japan, dark fantasy for Korea — produced by native designers.",
      "Apple Search Ads Discovery campaigns in both markets to capture new-launch intent queries.",
    ],
    results: [
      { value: "Top 10", label: "App Store chart (KR + JP)" },
      { value: "45 days", label: "time to chart position" },
      { value: "2M+", label: "influencer content views" },
      { value: "+68%", label: "Day-1 retention vs global avg" },
    ],
    quote: {
      text: "East Asia is a graveyard for Western studios. AppFillip's local-first approach got us to Top 10 in 45 days — something our in-house team had tried and failed for a year.",
      author: "Publishing Director, RPG Game",
    },
  },

  {
    slug: "sports-game-influencer",
    category: "Gaming",
    appName: "Sports Game",
    title: "5M+ influencer reach for a sports game launch",
    metric: "5M+",
    metricLabel: "influencer content reach",
    summary:
      "Influencer Marketing and Paid Social drove 5M+ content views and a record launch-day install spike for a sports game across USA, UK, Brazil and India.",
    market: "USA · UK · Brazil · India",
    platform: "both",
    services: ["Influencer Marketing", "Paid Social", "ASO"],
    duration: "Launch campaign (6 weeks)",
    challenge:
      "A new sports game had a strong IP but no existing user base. Launch awareness was the bottleneck — paid alone couldn't create the social credibility the category demanded.",
    approach: [
      "Recruited 40 sports + gaming influencers across four markets — mix of mega (×2), macro (×8) and micro (×30).",
      "Content brief: authentic gameplay highlight reels, not scripted ads — aligned to each creator's voice.",
      "Paid Social amplification: boosted top influencer posts as dark ads to cold audiences in all four markets.",
      "ASO timed to launch: keyword set optimised around sport-specific queries + trending search terms.",
    ],
    results: [
      { value: "5M+", label: "influencer content reach" },
      { value: "#1", label: "Free Games chart on launch day (USA)" },
      { value: "+380%", label: "Day-1 installs vs projection" },
      { value: "4.7★", label: "launch-week average rating" },
    ],
    quote: {
      text: "We'd budgeted for a decent launch. AppFillip delivered a record one — #1 Free Games in the US on day one is not something we expected.",
      author: "Launch Producer, Sports Game",
    },
  },

  // ─── HEALTH & WELLNESS ───────────────────────────────────────────────────

  {
    slug: "fitness-app-influencer-aso",
    category: "Health",
    appName: "Fitness App",
    title: "+188% trial starts for a fitness app via influencer + ASO",
    metric: "+188%",
    metricLabel: "free trial starts",
    summary:
      "Influencer Marketing and ASO combined to nearly triple free trial starts for a fitness app across USA, UK and Australia — without increasing paid budgets.",
    market: "USA · UK · Australia",
    platform: "ios",
    services: ["Influencer Marketing", "ASO", "App Retention"],
    duration: "4 months",
    challenge:
      "A fitness subscription app was growing install volume via paid but trial-to-paid conversion was flat. Organic discovery was weak and influencer efforts had been haphazard with no measurable ROI.",
    approach: [
      "ASO rebuild focused on conversion — screenshot A/B tests showing transformation outcomes before/after testimonial format.",
      "Influencer program: 25 fitness creators (health/workout niche) posting authentic 30-day challenge content.",
      "UTM-tagged influencer links to track trial starts per creator — data-driven creator roster optimisation.",
      "Retention: post-install push sequence (Day 1, 3, 7, 14) anchored to workout milestones to reduce D7 churn.",
    ],
    results: [
      { value: "+188%", label: "free trial starts" },
      { value: "+62%", label: "trial-to-paid conversion" },
      { value: "+44%", label: "D30 retention" },
      { value: "−22%", label: "paid acquisition spend" },
    ],
    quote: {
      text: "Influencer content that actually drove trials, not just views. AppFillip built the measurement layer that let us finally see which creators moved the needle.",
      author: "CMO, Fitness App",
    },
  },

  {
    slug: "fitness-app-search-led-aso-growth",
    category: "Health",
    appName: "Fitness App",
    title: "+185% organic installs for a fitness app",
    metric: "+185%",
    metricLabel: "organic installs",
    summary:
      "A search-led growth program - built around workout discovery, seasonal demand, stronger first-impression creatives and category-specific positioning - helped a fitness app generate 185% more organic installs across the USA, UK and Canada.",
    market: "USA · UK · Canada",
    platform: "both",
    services: [
      "ASO",
      "Search Discovery",
      "Creative Testing",
      "Seasonal Optimization",
      "Analytics",
    ],
    duration: "90 days",
    challenge:
      "The app already had a deep library of guided workouts, personalized plans and progress tools. The problem appeared before users ever experienced them: people searching for specific fitness goals simply weren't finding the app. Most visibility came from the brand name and a handful of broad fitness terms. Searches connected to actual user goals - home training, beginner workouts, strength routines and short daily sessions - were largely being captured by competitors. The listing also treated every visitor like the same user, despite someone searching for a 10 minute home workout having very different motivation from someone looking for a strength training plan.",
    approach: [
      "Segmented searches by fitness goal: searches were grouped around distinct motivations including lose weight, build strength, exercise at home, start working out, train without equipment and stay consistent.",
      "Found the gaps big fitness brands were leaving open: competitor visibility was analyzed to identify useful queries where dominant fitness apps were strong on broad terms but weaker on specific workout needs.",
      "Made the first screenshot answer one question: the opening creative was redesigned around whether the workout app was actually for that user, with messaging tested across home workouts, personalized plans, quick sessions and visible progress.",
      "Matched the listing to seasonal fitness demand: the store strategy was adjusted around demand spikes such as New Year fitness goals, summer workouts and at-home training periods.",
      "Turned early ranking wins into bigger opportunities: targeting expanded from lower-competition terms toward more competitive searches once initial ranking authority improved.",
      "Used conversion data to decide what stayed: screenshot messaging and positioning were kept, replaced or expanded according to their effect on product-page conversion and organic acquisition.",
    ],
    results: [
      { value: "+185%", label: "organic installs" },
      { value: "73", label: "new keywords in top-10" },
      { value: "+44%", label: "product page conversion" },
      { value: "+61%", label: "non-branded discovery" },
    ],
    quote: {
      text: "The biggest change wasn't one keyword jumping in rank. We started appearing across dozens of different ways people actually search for workouts, and those users converted.",
      author: "Growth Manager, Fitness App",
    },
  },

  {
    slug: "meditation-wellness-app-rating-growth",
    category: "Health",
    appName: "Meditation App",
    title: "3.6★ to 4.7★ average rating for a meditation app",
    metric: "4.7★",
    metricLabel: "avg rating (was 3.6★)",
    summary:
      "A reputation-led growth program - combining review intelligence, friction diagnosis, rating-prompt timing and store trust signals - helped a meditation app move from 3.6★ to 4.7★ while strengthening user confidence across the USA, Canada and Australia.",
    market: "USA · Canada · Australia",
    platform: "both",
    services: [
      "Ratings & Reviews",
      "Review Intelligence",
      "Reputation Strategy",
      "Conversion Analysis",
      "ASO",
    ],
    duration: "100 days",
    challenge:
      "The app had built a loyal audience around guided meditation, sleep sessions and mindfulness routines, but its public store reputation wasn't reflecting the experience of its most engaged users. Negative reviews repeatedly mentioned onboarding confusion, subscription expectations and difficulty finding the right sessions. At the same time, satisfied long-term users rarely left ratings, creating an imbalance between actual user satisfaction and what prospective users saw on the store page. For an app asking people to build a daily wellness habit, a 3.6★ rating was becoming a trust barrier before the first session even began.",
    approach: [
      "Turned thousands of reviews into themes: existing feedback was classified by recurring topic across subscription clarity, onboarding, content discovery, session preferences and technical friction.",
      "Separated product problems from expectation problems: unclear expectations were addressed through store messaging and communication, while genuine usability issues were routed to the product team.",
      "Changed when satisfied users saw the rating prompt: prompt opportunities moved toward meaningful positive milestones such as completing sessions or establishing repeat usage.",
      "Built a response system around the reason for the review: billing concerns, technical problems, content requests and usability complaints followed different response paths.",
      "Closed the loop between reviews and product decisions: recurring feedback themes were regularly surfaced to product and support teams.",
      "Made reputation part of store conversion: as ratings improved, stronger trust signals were incorporated into the listing experience.",
    ],
    results: [
      { value: "4.7★", label: "avg rating (was 3.6★)" },
      { value: "+214%", label: "5-star review volume" },
      { value: "-63%", label: "1-2★ review share" },
      { value: "+34%", label: "visitor-to-install rate" },
    ],
    quote: {
      text: "We thought we had a review problem. What we actually had was a feedback problem. Once we understood why people were leaving low ratings, we could improve the experience and ask happy users at the right moment.",
      author: "Customer Experience Lead, Meditation App",
    },
  },

  {
    slug: "mental-wellness-app-retention",
    category: "Health",
    appName: "Mental Wellness App",
    title: "+85% D30 retention for a mental wellness app",
    metric: "+85%",
    metricLabel: "D30 retention improvement",
    summary:
      "App Retention, Influencer Marketing and ASO rebuilt the user journey for a mental wellness app — D30 retention nearly doubled and subscription revenue grew 2× across USA, Australia and UK.",
    market: "USA · Australia · UK · Canada",
    platform: "both",
    services: ["App Retention", "Influencer Marketing", "ASO"],
    duration: "5 months",
    challenge:
      "A mental wellness app had high D1 retention but fell off a cliff by D7. Push notifications were generic, onboarding was one-size-fits-all, and there was no lifecycle program beyond the install.",
    approach: [
      "Onboarding redesign: mood-based personalisation at first launch → surfaced relevant content type per user goal.",
      "Lifecycle push + email sequences segmented by: goal type, session frequency, subscription status.",
      "Influencer program with mindfulness and mental health creators sharing authentic daily-use routines.",
      "ASO rewrite: shifted from feature-led to benefit-led — 'feel calmer', 'sleep better', 'reduce anxiety'.",
    ],
    results: [
      { value: "+85%", label: "D30 retention" },
      { value: "2×", label: "subscription revenue" },
      { value: "+48%", label: "D7 active users" },
      { value: "+55%", label: "session frequency per user" },
    ],
    quote: {
      text: "We were a leaky bucket — high installs, terrible retention. AppFillip plugged the lifecycle holes and doubled subscription revenue in five months.",
      author: "Head of Product, Mental Wellness App",
    },
  },

  {
    slug: "telemedicine-app-paid-search",
    category: "Health",
    appName: "Telemedicine App",
    title: "+118% qualified installs for a telemedicine app",
    metric: "+118%",
    metricLabel: "qualified install growth",
    summary:
      "Paid Search, SEO and Paid Social grew qualified installs (users who completed a first consultation) by 118% for a telemedicine app across USA, Canada and Australia.",
    market: "USA · Canada · Australia",
    platform: "both",
    services: ["Paid Search", "SEO", "Paid Social"],
    duration: "6 months",
    challenge:
      "A telemedicine app had high install volume from broad campaigns but first-consultation rates were poor — most installs churned before completing onboarding. CAC was high for low downstream revenue.",
    approach: [
      "Paid Search restructured around high-intent symptom + treatment queries: 'online doctor', 'GP appointment same day', 'urgent care app'.",
      "Negative keyword hygiene removed research-intent and non-commercial queries that inflated volume without quality.",
      "SEO content program around 'see a doctor without leaving home' and condition-specific landing pages.",
      "Paid Social retargeting of engaged non-converters with social proof creative (doctor head count, avg wait time).",
    ],
    results: [
      { value: "+118%", label: "qualified installs" },
      { value: "−40%", label: "CPA for first consultation" },
      { value: "+95%", label: "non-brand SEO traffic" },
      { value: "+34%", label: "first-consultation completion rate" },
    ],
    quote: {
      text: "We were optimising for install volume. AppFillip shifted us to quality — first-consultation CAC fell 40% and the cohorts are vastly better.",
      author: "Growth Director, Telemedicine App",
    },
  },

  // ─── EDTECH ──────────────────────────────────────────────────────────────

  {
    slug: "exam-prep-app-peak-season-organic-installs",
    category: "EdTech",
    appName: "Exam Prep App",
    title: "+260% organic installs during peak exam season",
    metric: "+260%",
    metricLabel: "organic installs",
    summary:
      "A seasonality-driven growth strategy - combining exam-specific discovery, preparation-stage targeting, deadline-aware storefront updates and high-intent creative messaging - helped an exam preparation app achieve +260% organic installs during its most competitive acquisition period across India.",
    market: "India",
    platform: "both",
    services: [
      "Seasonal ASO",
      "Search Intelligence",
      "Creative Optimization",
      "Conversion Testing",
      "Analytics",
    ],
    duration: "90 days",
    challenge:
      "The app offered mock tests, practice questions, revision material and performance tracking for students preparing for competitive exams. But demand wasn't consistent throughout the year. Search activity accelerated dramatically as application dates, exam schedules and final preparation periods approached. The existing listing stayed almost identical year-round, meaning the app wasn't reacting when students shifted from broad research toward urgent searches around mock tests, previous papers, revision, practice questions and specific exams. Missing the peak didn't simply mean slower growth. It meant waiting another year for the same demand cycle.",
    approach: [
      "Mapped demand against exam dates: search opportunities were organized according to upcoming exam cycles rather than managed as a static annual keyword set.",
      "Separated students by preparation stage: discovery was divided into stages from starting preparation to learning concepts, practising questions, taking mock tests, revising and final preparation.",
      "Created exam-specific discovery routes: targeting expanded toward individual exam categories and combinations such as exam name plus mock test, practice questions, preparation and previous papers.",
      "Made the storefront feel timely: creative messaging changed as exam dates moved closer, shifting from structured preparation and syllabus coverage to mock tests, revision and readiness.",
      "Turned progress into the creative hook: mock scores, progress insights and preparation tools became part of the visual story.",
      "Shifted fast as search demand peaked: performance was reviewed more frequently during high-demand weeks so metadata and creative priorities could respond while the opportunity was active.",
    ],
    results: [
      { value: "+260%", label: "organic installs" },
      { value: "3.4x", label: "exam-related search impressions" },
      { value: "+84%", label: "store conversion" },
      { value: "+71%", label: "organic registrations" },
    ],
    quote: {
      text: "Exam demand has always been seasonal, but our App Store strategy wasn't. This time the listing changed with the student's preparation journey, and we captured significantly more of the demand when it mattered most.",
      author: "Growth & Acquisition Lead, Exam Prep App",
    },
  },

  {
    slug: "language-learning-app-search-visibility",
    category: "EdTech",
    appName: "Language Learning App",
    title: "+240% search visibility for a language learning app",
    metric: "+240%",
    metricLabel: "search visibility",
    summary:
      "A multilingual discovery strategy - built around language-specific search behaviour, localized store presence, intent-based keyword targeting and market-relevant creatives - helped a language learning app achieve +240% search visibility across India, the USA and Canada.",
    market: "India · USA · Canada",
    platform: "both",
    services: [
      "ASO",
      "Localization",
      "Keyword Research",
      "Creative Strategy",
      "Market Expansion",
    ],
    duration: "120 days",
    challenge:
      "The app supported multiple languages and learning journeys, but its store presence treated language learning as a single search category. Users weren't searching in one predictable way. Some wanted to learn English, others searched for spoken English practice, Spanish lessons, Hindi learning, vocabulary practice or tools for everyday conversation. A largely uniform store strategy meant the app was missing thousands of searches shaped by language, location and learning objective. Expansion into new markets was increasing the size of the opportunity - but not the app's share of it.",
    approach: [
      "Built search clusters around the language being learned: discovery was separated by individual language demand rather than placing every query inside one broad education keyword pool.",
      "Separated beginner intent from practice intent: queries were classified by learning stage so the listing could capture users looking to start, improve, practise or become conversational.",
      "Adapted discovery to each market: market-level research identified differences in language demand, terminology and competition across India, the USA and Canada.",
      "Made each screenshot sell a learning moment: creatives were organized around recognizable learning situations like speaking a first sentence, practising real conversations, improving pronunciation and building vocabulary every day.",
      "Opened long-tail discovery paths: the strategy expanded into specific combinations of language, goal and learning method instead of relying only on broad searches such as language learning.",
      "Expanded winning clusters language by language: ranking gains were evaluated separately for each language cluster, then adjacent queries were introduced where traction was strongest.",
    ],
    results: [
      { value: "+240%", label: "search visibility" },
      { value: "112", label: "new keywords in top-20" },
      { value: "+76%", label: "non-branded installs" },
      { value: "3.1x", label: "localized search impressions" },
    ],
    quote: {
      text: "We used to think about ASO as ranking for language learning. The real opportunity was hundreds of smaller searches around what someone wanted to learn, how they wanted to practise and where they lived.",
      author: "Marketing Lead, Language Learning App",
    },
  },

  {
    slug: "learning-app-india-aso",
    category: "EdTech",
    appName: "Learning App",
    title: "+220% iOS growth for a learning app across India, USA and UK",
    metric: "+220%",
    metricLabel: "overall iOS app growth",
    summary:
      "ASO, Apple Search Ads and CRO grew an AI-powered learning app by 220% — from invisible to category contender — without scaling paid spend.",
    market: "India · USA · UK",
    platform: "ios",
    services: ["ASO", "User Acquisition", "Apple Search Ads"],
    duration: "90 days",
    challenge:
      "A well-built learning app had weak App Store visibility in a fast-growing AI education category. Keyword rankings were stagnant, organic discovery was thin and CPIs were unsustainable.",
    approach: [
      "Rebuilt the keyword stack across AI tutoring, subject-specific homework help and step-by-step solving intents for each market.",
      "Rewrote title, subtitle and screenshots to lead with the AI-step-by-step USP — benefit-first not feature-first.",
      "Apple Search Ads restructured into Discovery / Exact / Competitor ad groups to control CPI.",
      "Custom Product Pages A/B-tested per traffic source — ASO organic vs. paid social audiences.",
    ],
    results: [
      { value: "+220%", label: "App Store impressions" },
      { value: "+85%", label: "iOS install growth" },
      { value: "+140%", label: "organic keyword rankings" },
      { value: "+220%", label: "overall iOS app growth" },
    ],
    quote: {
      text: "AppFillip moved every metric that mattered — impressions, organic ranks and install velocity — without forcing us to scale paid recklessly.",
      author: "Growth Team, Learning App",
    },
  },

  {
    slug: "corporate-learning-app-gtm",
    category: "EdTech",
    appName: "Corporate Learning App",
    title: "B2B app launch: 10K enterprise users in 90 days",
    metric: "10K",
    metricLabel: "enterprise users in 90 days",
    summary:
      "GTM Strategy, Paid Search and SEO launched a corporate learning app into USA, UK and Germany — hitting 10,000 enterprise users in 90 days with a below-target cost per activated user.",
    market: "USA · UK · Germany",
    platform: "both",
    services: ["GTM Strategy", "Paid Search", "SEO", "App Retention"],
    duration: "90-day launch",
    challenge:
      "A corporate learning platform was pivoting from web to mobile-first. B2B app acquisition is structurally different from B2C — longer sales cycles, procurement gatekeeping, IT-security objections.",
    approach: [
      "GTM positioning: 'the mobile LMS that IT approves in a day' — enterprise-security compliance as hero benefit.",
      "Paid Search targeting HR, L&D and Operations job titles on LinkedIn + Google, not broad keywords.",
      "SEO content around 'mobile LMS comparison', 'corporate training app', 'employee learning app' commercial queries.",
      "Post-signup retention: onboarding checklist, first-course-complete nudge, team-invite prompt at Day 3.",
    ],
    results: [
      { value: "10K", label: "enterprise users in 90 days" },
      { value: "−30%", label: "cost per activated user vs target" },
      { value: "3", label: "markets launched simultaneously" },
      { value: "+68%", label: "D30 course-completion rate" },
    ],
    quote: {
      text: "B2B app launches are a completely different game. AppFillip understood the enterprise buyer journey and built acquisition around it — not around consumer app playbooks.",
      author: "VP Marketing, Corporate Learning App",
    },
  },

  // ─── eCOMMERCE ───────────────────────────────────────────────────────────

  {
    slug: "shopping-app-store-conversion",
    category: "eCommerce",
    appName: "Shopping App",
    title: "+68% store conversion for a shopping app",
    metric: "+68%",
    metricLabel: "store conversion",
    summary:
      "A merchandising-led conversion program - combining storefront hierarchy, purchase-intent messaging, social-proof placement and visual experimentation - helped an ecommerce app achieve +68% store conversion across the USA, UK and UAE.",
    market: "USA · UK · UAE",
    platform: "both",
    services: [
      "Conversion Optimization",
      "Creative Testing",
      "ASO",
      "Store Merchandising",
      "Acquisition Analytics",
    ],
    duration: "90 days",
    challenge:
      "The shopping app offered a large catalog, competitive pricing and frequent deals, but its store listing looked more like a product catalog than a compelling mobile shopping experience. Visitors were immediately shown multiple categories, discounts and interface screens without a clear hierarchy. The strongest reasons to choose the app - exclusive app offers, faster product discovery, personalized recommendations and convenient checkout - were buried deeper in the listing. Traffic wasn't the primary constraint. Too many high-intent shoppers were reaching the store page and leaving before installation.",
    approach: [
      "Identified what made shoppers cross the install barrier: app-only deals, personalized discovery, saved favorites, order tracking and faster repeat purchases emerged as stronger installation triggers than catalog size.",
      "Gave the first three screenshots different jobs: the opening gallery established the shopping promise, demonstrated breadth and discovery, then introduced the strongest convenience benefit.",
      "Turned product categories into acquisition doors: fashion, beauty, electronics, home and other high-performing categories were treated as separate discovery opportunities.",
      "Brought trust closer to the decision: ratings, delivery expectations, easy returns and secure checkout signals were surfaced where users were most likely to question whether they should install.",
      "Tested value against discount: creative experiments compared deal-led, convenience-led, selection-led and personalized-shopping propositions.",
      "Reduced the gap between ad and app store: high-performing acquisition creatives were mapped to corresponding store messages by category and shopping context.",
    ],
    results: [
      { value: "+68%", label: "store conversion" },
      { value: "+91%", label: "category-led installs" },
      { value: "+36%", label: "first-purchase rate" },
      { value: "-21%", label: "cost per new customer" },
    ],
    quote: {
      text: "We were showing people everything our app had instead of giving them one strong reason to install. Simplifying that decision improved not only installs, but the number of new users who actually purchased.",
      author: "Ecommerce Growth Manager, Shopping App",
    },
  },

  {
    slug: "shopping-app-paid-search-seo",
    category: "eCommerce",
    appName: "Shopping App",
    title: "+195% qualified installs for a shopping app via paid + SEO",
    metric: "+195%",
    metricLabel: "qualified installs",
    summary:
      "Paid Search, Paid Social and SEO grew qualified installs (users making a first purchase) by 195% for a shopping app across USA, Canada and Australia.",
    market: "USA · Canada · Australia",
    platform: "both",
    services: ["Paid Search", "Paid Social", "SEO"],
    duration: "6 months",
    challenge:
      "A shopping app was running broad campaigns with acceptable install CPIs but first-purchase rates were poor. SEO had been neglected entirely and there was no intent-layered paid structure.",
    approach: [
      "Paid Search: intent-segmented into brand defence, category comparison and product-specific queries.",
      "Paid Social: separate funnels for cold (new user acquisition), warm (app installs with no purchase) and lapsed (D7+ no purchase).",
      "SEO program targeting '[product category] app', 'best deals on [category]' commercial-intent queries.",
      "Dynamic product ads on Meta and Google Shopping feeding real-time inventory to highest-intent audiences.",
    ],
    results: [
      { value: "+195%", label: "qualified installs (first-purchase users)" },
      { value: "−42%", label: "first-purchase CAC" },
      { value: "+200%", label: "non-brand SEO traffic" },
      { value: "+38%", label: "30-day revenue per cohort" },
    ],
    quote: {
      text: "Install volume was never the problem. AppFillip fixed the quality — first-purchase CAC dropped 42% and cohort revenue is up 38%.",
      author: "Head of eCommerce Growth, Shopping App",
    },
  },

  {
    slug: "fashion-app-influencer-uae-india",
    category: "eCommerce",
    appName: "Fashion App",
    title: "+280% app installs for a fashion app via influencer marketing",
    metric: "+280%",
    metricLabel: "app installs",
    summary:
      "Influencer Marketing, Paid Social and ASO drove a 280% install spike for a fashion app across UAE, India and UK — with influencer content achieving 3× better CVR than brand creative.",
    market: "UAE · India · UK",
    platform: "both",
    services: ["Influencer Marketing", "Paid Social", "ASO"],
    duration: "3 months",
    challenge:
      "A fashion app was losing ground to well-funded competitors. Brand creative underperformed; the team needed authentic social proof from trusted fashion voices in three culturally distinct markets.",
    approach: [
      "Market-specific influencer rosters: Dubai-based fashion micro-influencers (UAE), Hindi-language fashion creators (India), lifestyle bloggers (UK).",
      "Content framework: outfit-of-the-day + in-app browse reveal — native format per platform (Reels, TikTok, YouTube Shorts).",
      "Paid Social amplification of top-performing organic influencer posts as acquisition ads.",
      "ASO: keyword set updated to include trending fashion terms surfaced from influencer content engagement data.",
    ],
    results: [
      { value: "+280%", label: "app installs" },
      { value: "3×", label: "influencer creative CVR vs brand ads" },
      { value: "+95%", label: "social-driven revenue" },
      { value: "4.5★", label: "avg rating (was 3.8★)" },
    ],
    quote: {
      text: "Our brand ads never performed. AppFillip's influencer program showed us what authentic creative actually looks like — and installs almost tripled.",
      author: "Digital Director, Fashion App",
    },
  },

  {
    slug: "grocery-delivery-app-retention",
    category: "eCommerce",
    appName: "Grocery Delivery App",
    title: "+70% repeat orders via retention programme for a grocery app",
    metric: "+70%",
    metricLabel: "repeat order rate",
    summary:
      "App Retention, Paid Social and User Acquisition built a full lifecycle programme for a grocery delivery app — repeat orders rose 70% and CAC dropped 35% via subscription conversion.",
    market: "India · UAE · UK",
    platform: "both",
    services: ["App Retention", "Paid Social", "User Acquisition"],
    duration: "4 months",
    challenge:
      "A grocery delivery app had strong install volumes but order frequency was low and subscriber conversion was poor. Every month was effectively re-acquiring the same single-order users.",
    approach: [
      "Lifecycle segmentation: first-time users, 1-3 orders, 4+ orders, lapsed — separate push, email and in-app message flows.",
      "Subscription upsell prompt tested at optimal timing (post-third-order) with personalised save-per-order calculation.",
      "Paid Social re-engagement campaigns for lapsed users (no order in 14 days) with personalised category offers.",
      "Win-back email series for churned subscribers: discount ladder over 7, 14, 21 days.",
    ],
    results: [
      { value: "+70%", label: "repeat order rate" },
      { value: "+45%", label: "subscription conversion" },
      { value: "−35%", label: "blended CAC (via subscription LTV)" },
      { value: "+52%", label: "6-month revenue per cohort" },
    ],
    quote: {
      text: "We were re-acquiring the same customers every month. AppFillip's lifecycle programme turned one-time buyers into subscribers — CAC effectively fell by a third.",
      author: "Head of Growth, Grocery Delivery App",
    },
  },

  // ─── FOOD & LIFESTYLE ────────────────────────────────────────────────────

  {
    slug: "food-delivery-app-non-branded-installs",
    category: "Food & Delivery",
    appName: "Food Delivery App",
    title: "+175% non-branded installs for a food delivery app",
    metric: "+175%",
    metricLabel: "non-branded installs",
    summary:
      "A local discovery program - combining cuisine-led search targeting, city-level store positioning, competitive gap analysis and occasion-based creatives - helped a food delivery app achieve +175% non-branded installs across India, UAE and Singapore.",
    market: "India · UAE · Singapore",
    platform: "both",
    services: [
      "ASO",
      "Localization",
      "Creative Optimization",
      "Competitor Analysis",
      "User Acquisition",
    ],
    duration: "120 days",
    challenge:
      "The food delivery app had a growing restaurant network, competitive delivery times and attractive first-order offers, yet discovery depended heavily on users already knowing the brand. Generic searches around food delivery, restaurants nearby, grocery delivery and specific cuisines were consistently dominated by larger platforms. Even when users reached the listing, promotional banners and restaurant imagery made the page feel interchangeable with competitors. The opportunity was to capture customers at the moment they decided what, when and where they wanted to order.",
    approach: [
      "Mapped searches around ordering moments: search demand was divided according to real ordering behaviour, including late-night food, lunch delivery, dinner, quick meals, groceries, nearby restaurants and cuisine-specific orders.",
      "Found local opportunities city by city: search patterns were evaluated separately across target markets to uncover differences in cuisine demand, terminology and competitor strength.",
      "Built cuisine-led discovery paths: specific searches for pizza, burgers, Indian food, Chinese food, desserts and other relevant cuisines created new opportunities closer to an ordering decision.",
      "Redesigned the listing around speed and choice: the creative sequence emphasized restaurants near you, fast delivery, more food choices and easy ordering.",
      "Connected promotions to user intent: first-order offers were tested against convenience, restaurant choice and delivery-speed positioning to understand what actually drove installs.",
      "Aligned acquisition with the store experience: paid campaigns and product-page messaging were coordinated around matching ordering occasions.",
    ],
    results: [
      { value: "+175%", label: "non-branded installs" },
      { value: "+128%", label: "local search visibility" },
      { value: "+54%", label: "store conversion rate" },
      { value: "-24%", label: "cost per first order" },
    ],
    quote: {
      text: "Our brand searches were healthy, but they only reached people who already knew us. The breakthrough came when we started appearing at the exact moment someone was deciding what to order.",
      author: "Acquisition Lead, Food Delivery App",
    },
  },

  {
    slug: "food-delivery-app-india-mena",
    category: "Food & Delivery",
    appName: "Food Delivery App",
    title: "+148% installs for a food delivery app across India and MENA",
    metric: "+148%",
    metricLabel: "install growth",
    summary:
      "User Acquisition, Paid Social and ASO grew installs by 148% for a food delivery app across India (Tier 1 + Tier 2 cities) and three MENA markets with a city-level campaign architecture.",
    market: "India · UAE · Saudi Arabia · Kuwait",
    platform: "both",
    services: ["User Acquisition", "Paid Social", "ASO"],
    duration: "5 months",
    challenge:
      "A food delivery app was running single-level national campaigns in India — bleeding budget on Tier 1 cities while Tier 2 was ignored. MENA expansion had stalled at planning stage.",
    approach: [
      "India: city-tier campaign split — Tier 1 on premium cuisine creative, Tier 2 on value and speed messaging.",
      "MENA: Arabic-language Paid Social creative for UAE/KSA, English for Kuwait — restaurant-category specific funnels.",
      "ASO localisation: Arabic metadata for MENA, Hindi + English for India — separate keyword stacks.",
      "First-order incentive creative A/B test: % discount vs. fixed-amount voucher vs. free item — fixed-amount won in all markets.",
    ],
    results: [
      { value: "+148%", label: "install growth" },
      { value: "4", label: "new markets activated" },
      { value: "−29%", label: "blended CPI" },
      { value: "+48%", label: "first-order completion rate" },
    ],
    quote: {
      text: "City-level campaigns sound obvious. AppFillip is the first team that actually built them — Tier 2 India alone is now 40% of our volume.",
      author: "Growth Manager, Food Delivery App",
    },
  },

  {
    slug: "photo-video-editor-app-organic-downloads",
    category: "Lifestyle",
    appName: "Photo & Video Editor App",
    title: "+190% organic downloads for a photo and video editing app",
    metric: "+190%",
    metricLabel: "organic downloads",
    summary:
      "A creator-focused discovery campaign - combining editing-intent segmentation, trend-responsive search coverage, visual proof-of-output and feature-specific positioning - helped a photo and video editor achieve +190% organic downloads across the USA, Brazil and Mexico.",
    market: "USA · Brazil · Mexico",
    platform: "both",
    services: [
      "ASO",
      "Trend Research",
      "Creative Optimization",
      "Localization",
      "Conversion Testing",
    ],
    duration: "120 days",
    challenge:
      "The app offered filters, templates, background removal, retouching, video effects and social-ready editing tools. But its store presence compressed all of that capability into broad photo editor and video editor positioning. That placed it against some of the largest creative apps in the category while overlooking users searching for a specific editing outcome. The challenge wasn't convincing people that editing apps were useful. It was making this particular app appear when creators knew exactly what they wanted to make.",
    approach: [
      "Rebuilt discovery around editing actions: search demand was organized around background removal, photo enhancement, filters, portrait retouching, video effects, templates, collages and social content creation.",
      "Matched searches to the right tool: individual features were connected to the search intent they could satisfy best, from cutouts to retouching to short-form content creation.",
      "Let the output sell the product: new creatives gave greater prominence to before-and-after transformations, finished edits and visible creative outcomes.",
      "Built a search radar for creative trends: emerging editing styles, creator terminology and feature searches were reviewed throughout the campaign.",
      "Localized creator language, not just store copy: Brazil and Mexico storefronts were shaped around local search phrasing and creator terminology.",
      "Used winning outputs to shape the next test: creative experiments informed subsequent screenshots and positioning based on the finished content users found most compelling.",
    ],
    results: [
      { value: "+190%", label: "organic downloads" },
      { value: "+162%", label: "non-branded discovery" },
      { value: "96", label: "new keywords in top-10" },
      { value: "+52%", label: "store-to-install conversion" },
    ],
    quote: {
      text: "Our biggest features were already inside the product. The growth came from turning each one into a way for creators to find us. We stopped looking like another editor and started showing up for the exact edits people wanted to make.",
      author: "Mobile Growth Lead, Photo & Video Editor App",
    },
  },

  {
    slug: "recipe-app-influencer-content",
    category: "Lifestyle",
    appName: "Recipe App",
    title: "+350% organic traffic for a recipe app via content + influencer",
    metric: "+350%",
    metricLabel: "organic traffic growth",
    summary:
      "Content Marketing, Influencer Marketing and ASO drove a 350% organic traffic surge for a recipe app across USA, UK, India and Australia — subscription revenue grew 2× in six months.",
    market: "USA · UK · India · Australia",
    platform: "both",
    services: ["Content Marketing", "Influencer Marketing", "ASO"],
    duration: "6 months",
    challenge:
      "A recipe subscription app had a loyal base but organic growth was flat. The team published content inconsistently, had no influencer programme and was spending heavily on paid to make up the organic shortfall.",
    approach: [
      "SEO content programme: 80 long-form recipes + 'how to cook' guides targeting 500+ commercial-intent queries.",
      "Influencer programme: 30 food creators across four markets — recipe reveal content with in-app exclusive ingredients.",
      "ASO keyword refresh: recipe-type + cuisine + dietary-preference keyword clusters (vegan, keto, 15-minute meals).",
      "Content distribution: weekly email digest, push notification with trending recipe, social Reels series.",
    ],
    results: [
      { value: "+350%", label: "organic traffic" },
      { value: "2×", label: "subscription revenue" },
      { value: "+180%", label: "app store impressions" },
      { value: "−40%", label: "paid acquisition spend" },
    ],
    quote: {
      text: "Organic was an afterthought before AppFillip. Six months later it's our biggest growth channel and we halved our paid spend.",
      author: "Marketing Lead, Recipe App",
    },
  },

  {
    slug: "dating-app-paid-social-aso",
    category: "Lifestyle",
    appName: "Dating App",
    title: "+165% qualified installs for a dating app across 4 markets",
    metric: "+165%",
    metricLabel: "qualified installs",
    summary:
      "Paid Social, ASO and Ratings & Reviews drove qualified install growth for a dating app across USA, Brazil, France and Australia — with subscription trial conversion improving 55%.",
    market: "USA · Brazil · France · Australia",
    platform: "both",
    services: ["Paid Social", "ASO", "Ratings & Reviews"],
    duration: "4 months",
    challenge:
      "A dating app was growing installs but subscription trial rate was flat. Creative was generic ('meet new people'), ratings were sub-4★ and there was no localised strategy per market.",
    approach: [
      "Per-market Paid Social: USA on authentic user testimonials, Brazil on vibrant social-proof creative, France on privacy messaging, Australia on outdoor lifestyle.",
      "ASO: separate keyword builds per market — localised intent (rencontres France, encontros Brasil) + universal english terms.",
      "Review response programme: proactive outreach to 5★ users for written review — 4.4★ avg achieved.",
      "Subscription trial A/B test: 3-day vs 7-day vs. feature-gated trial — 7-day drove highest paid conversion.",
    ],
    results: [
      { value: "+165%", label: "qualified installs" },
      { value: "+55%", label: "subscription trial conversion" },
      { value: "4.4★", label: "avg rating (was 3.7★)" },
      { value: "4", label: "markets with localised strategy" },
    ],
    quote: {
      text: "Generic 'meet new people' ads don't work anymore. AppFillip built four distinct market strategies and our trial conversion jumped 55%.",
      author: "Growth Director, Dating App",
    },
  },

  // ─── PRODUCTIVITY ────────────────────────────────────────────────────────

  {
    slug: "ai-productivity-app-keyword-rankings",
    category: "Productivity",
    appName: "AI Productivity App",
    title: "+280% Top-20 keyword rankings for an AI productivity app",
    metric: "+280%",
    metricLabel: "top-20 keyword rankings",
    summary:
      "A category-positioning strategy - built around use-case discovery, AI search trends, competitor whitespace and task-focused store messaging - helped an AI productivity app achieve +280% Top-20 keyword rankings while competing in the rapidly expanding AI tools category across the USA, UK and Australia.",
    market: "USA · UK · Australia",
    platform: "both",
    services: [
      "ASO",
      "Category Positioning",
      "Search Intelligence",
      "Competitor Research",
      "Creative Strategy",
    ],
    duration: "100 days",
    challenge:
      "The product could summarize documents, generate content, answer questions, organize information and assist with everyday work. That flexibility was valuable inside the app - but difficult to communicate in the store. The listing attempted to rank around broad AI terminology while hundreds of new competitors were targeting the same searches. Meanwhile, users increasingly searched for specific tasks they wanted AI to perform, rather than simply looking for an AI app. The product had dozens of useful capabilities. The store listing needed to turn those capabilities into discoverable reasons to install.",
    approach: [
      "Broke AI demand into real user jobs: search demand was divided according to what users were trying to accomplish, including AI writing, document summarization, question answering, note assistance, research, brainstorming and everyday productivity.",
      "Found search territory between AI giants: competitor coverage was mapped to identify task-specific queries where large AI platforms had visibility but had not established overwhelming ranking dominance.",
      "Connected features to searchable problems: technical feature descriptions were translated into user-intent language such as summarize a long PDF, understand documents faster and find answers from your files.",
      "Made every screenshot answer what can I do with this: the gallery was organized as a collection of useful outcomes rather than a tour of the interface.",
      "Tracked fast-moving AI search behaviour: emerging queries, declining terminology and competitor movements were reviewed throughout the campaign.",
      "Created a path from specific tasks to broader terms: early traction was built through focused use-case queries, then expanded toward broader productivity and AI-assistant searches.",
    ],
    results: [
      { value: "+280%", label: "top-20 keyword rankings" },
      { value: "4.2x", label: "AI use-case visibility" },
      { value: "+103%", label: "search-driven installs" },
      { value: "+49%", label: "first-time visitor conversion" },
    ],
    quote: {
      text: "Our app could do a lot, but that was exactly the problem with our store positioning. Once every capability became a specific reason to discover us, search started becoming a meaningful acquisition channel.",
      author: "Product Marketing Lead, AI Productivity App",
    },
  },

  {
    slug: "business-saas-app-paid-dependency-reduction",
    category: "Productivity",
    appName: "Business SaaS App",
    title: "-35% paid acquisition dependency for a business SaaS app",
    metric: "-35%",
    metricLabel: "paid acquisition dependency",
    summary:
      "An organic acquisition program - combining high-intent search positioning, workflow-specific discovery, store conversion improvements and acquisition-channel rebalancing - helped a B2B SaaS app reduce its paid acquisition dependency by 35% while growing qualified organic adoption across the USA, UK and Canada.",
    market: "USA · UK · Canada",
    platform: "both",
    services: [
      "ASO",
      "Growth Strategy",
      "Conversion Optimization",
      "Acquisition Analytics",
      "Competitor Research",
    ],
    duration: "150 days",
    challenge:
      "The SaaS platform helped teams manage projects, assign work, collaborate and track progress, but mobile growth relied heavily on paid campaigns. Organic visibility remained concentrated around the company name, leaving valuable searches such as task management, team collaboration, project planning and work organization largely uncaptured. The result was an acquisition engine that could grow - but only when more budget was added. The goal was to build a stronger organic channel without sacrificing the quality of incoming business users.",
    approach: [
      "Mapped search demand to daily workflows: discovery opportunities were organized around planning projects, assigning tasks, managing teams, tracking deadlines, collaborating remotely and organizing daily work.",
      "Prioritized searches with business value: keywords were evaluated against the likelihood that the searcher could become a meaningful product user.",
      "Made the listing speak to teams, not features: revised creative demonstrated outcomes such as knowing what's due, keeping everyone aligned, managing work from anywhere and moving projects forward.",
      "Removed friction between discovery and trial: search source, product-page behaviour, onboarding and trial initiation were examined together to identify where qualified visitors dropped.",
      "Used paid search data to inform organic opportunity: high-converting messaging and search themes from acquisition campaigns were used to guide organic store visibility.",
      "Shifted budget as organic contribution grew: channel contribution was monitored and budget allocation adjusted gradually as organic acquisition produced more qualified users.",
    ],
    results: [
      { value: "-35%", label: "paid acquisition dependency" },
      { value: "+164%", label: "high-intent organic installs" },
      { value: "+72%", label: "trial starts from store search" },
      { value: "-26%", label: "cost per qualified signup" },
    ],
    quote: {
      text: "We didn't want to solve growth by continuously increasing spend. The real win was making organic discovery contribute users who were actually relevant to the product. That gave us a much healthier acquisition mix.",
      author: "VP Growth, Business SaaS App",
    },
  },

  {
    slug: "task-manager-app-aso-seo",
    category: "Productivity",
    appName: "Task Manager App",
    title: "+210% organic installs for a task manager app",
    metric: "+210%",
    metricLabel: "organic installs",
    summary:
      "ASO, SEO and Paid Search tripled organic installs for a task manager app across USA, UK, Canada and Australia — ranking in top 5 for 'to-do list app' across all markets.",
    market: "USA · UK · Canada · Australia",
    platform: "both",
    services: ["ASO", "SEO", "Paid Search"],
    duration: "4 months",
    challenge:
      "A well-designed task manager was buried in the most crowded App Store category. It ranked below page 3 for every high-intent productivity query. Web SEO was also negligible.",
    approach: [
      "Competitor keyword gap analysis: identified 40+ mid-tail queries where top competitors had weak metadata.",
      "Metadata rewrite: title and subtitle front-loaded with 'task manager', 'to-do list' and 'planner' clusters.",
      "Web SEO: 30+ product-comparison and how-to articles targeting 'best to-do app', 'task planner for teams' commercial queries.",
      "Apple Search Ads Exact campaigns on brand competitor terms where CVR data showed strong switch intent.",
    ],
    results: [
      { value: "+210%", label: "organic installs" },
      { value: "Top 5", label: "'to-do list app' rank in USA, UK, CA, AU" },
      { value: "+145%", label: "non-brand web traffic" },
      { value: "−31%", label: "paid acquisition spend" },
    ],
    quote: {
      text: "Four markets, top-5 rank for our category's most valuable keyword. AppFillip delivered what we'd been trying to do internally for two years.",
      author: "Founder, Task Manager App",
    },
  },

  {
    slug: "productivity-app-us-germany",
    category: "Productivity",
    appName: "Productivity App",
    title: "+155% qualified user growth for a productivity app",
    metric: "+155%",
    metricLabel: "qualified user growth",
    summary:
      "Paid Search, Paid Social and SEO grew a power-user productivity app's qualified user base by 155% across USA and Germany — targeting a narrow, high-LTV audience with surgical precision.",
    market: "USA · Germany · Canada",
    platform: "both",
    services: ["Paid Search", "Paid Social", "SEO"],
    duration: "6 months",
    challenge:
      "A productivity app with narrow but high-LTV audience (founders, executive assistants, operators) was burning budget on generic productivity ads. The target user was identifiable but unreached.",
    approach: [
      "Paid Search and Social: narrow job-title targeting + intent keyword pairs signalling workflow pain.",
      "Creator-led Paid Social featuring power-users demonstrating keyboard shortcuts and workflow automations.",
      "SEO programme: 'fastest [category] app', '[competitor] alternative', 'Mac [workflow type] shortcuts' commercial queries.",
      "German-language Paid Social and SEO content for DE market — not translated, locally written.",
    ],
    results: [
      { value: "+155%", label: "qualified user growth" },
      { value: "Top 3", label: "ranks on core category keywords" },
      { value: "+44%", label: "trial-to-paid conversion" },
      { value: "−28%", label: "blended CPA" },
    ],
    quote: {
      text: "Our audience is impossibly narrow. AppFillip is the first team that targeted them surgically instead of spraying budget at 'productivity'.",
      author: "Founder, Productivity App",
    },
  },

  // ─── TRAVEL ──────────────────────────────────────────────────────────────

  {
    slug: "travel-esim-app-global-search-visibility",
    category: "Travel",
    appName: "Travel eSIM App",
    title: "+220% global search visibility for a travel eSIM app",
    metric: "+220%",
    metricLabel: "global search visibility",
    summary:
      "An international ASO expansion - combining destination-specific discovery, traveler-intent targeting, localized storefronts and pre-trip creative positioning - helped a travel eSIM app achieve +220% global search visibility across the USA, UK, Australia, UAE and Singapore.",
    market: "USA · UK · Australia · UAE · Singapore",
    platform: "both",
    services: [
      "International ASO",
      "Localization",
      "Market Research",
      "Creative Strategy",
      "Conversion Optimization",
    ],
    duration: "150 days",
    challenge:
      "The app offered instant mobile data across dozens of international destinations, but its store presence depended too heavily on users already understanding what an eSIM was. Many travelers searched around the problem instead - internet abroad, travel data, international SIM, roaming alternatives or connectivity for a specific destination. The existing listing captured only a fraction of those journeys. With every destination creating its own search opportunity, a single global positioning strategy was leaving significant international demand untouched.",
    approach: [
      "Expanded discovery beyond eSIM: research focused on the problems travelers were trying to solve, including travel data, internet abroad, international connectivity, roaming, prepaid data and overseas SIM alternatives.",
      "Created destination-specific search territories: queries connected to Japan eSIM, USA data, Europe travel internet, Thailand SIM, Dubai connectivity and other destination combinations opened a larger international search footprint.",
      "Prioritized markets by travel opportunity: markets were evaluated using search demand, competitive pressure, travel relevance and existing performance.",
      "Sold the moment after landing: store creatives focused on the outcome of landing, connecting and moving quickly without roaming surprises or physical SIM friction.",
      "Adapted the message for different travelers: creative directions tested convenience, instant activation, roaming savings and multi-destination coverage across business, backpacker and family travel motivations.",
      "Captured demand before peak travel periods: destination priorities were adjusted ahead of summer travel, holiday periods and market-specific demand windows.",
    ],
    results: [
      { value: "+220%", label: "global search visibility" },
      { value: "186", label: "destination keywords in top-20" },
      { value: "+89%", label: "organic installs from travel search" },
      { value: "+43%", label: "store-to-install conversion" },
    ],
    quote: {
      text: "Our biggest realization was that travelers don't all search for an eSIM. They search for a way to stay connected wherever they're going. Once we built around that behaviour, every destination became a new acquisition opportunity.",
      author: "International Growth Lead, Travel eSIM App",
    },
  },

  {
    slug: "travel-app-influencer-global",
    category: "Travel",
    appName: "Travel App",
    title: "+320% app installs for a travel app via influencer + ASO",
    metric: "+320%",
    metricLabel: "app installs",
    summary:
      "Influencer Marketing, Paid Social and ASO drove a 320% install surge for a travel app across USA, UK, UAE and Singapore — with influencer content outperforming brand ads 4× on CVR.",
    market: "USA · UK · UAE · Singapore",
    platform: "both",
    services: ["Influencer Marketing", "Paid Social", "ASO"],
    duration: "4 months",
    challenge:
      "A travel app was competing with category giants on paid budgets it couldn't match. Brand awareness was low, ASO was unoptimised and influencer efforts had been ad-hoc without measurable attribution.",
    approach: [
      "35 travel micro and macro influencers across four markets — authentic travel vlog content integrating app booking moments.",
      "UTM tracking per influencer for transparent ROI: installs, first-booking rate, revenue per creator attributed.",
      "Paid Social amplification: boost top-performing influencer posts to cold lookalike audiences.",
      "ASO: keyword strategy around destination-specific travel queries + booking-intent terms per market.",
    ],
    results: [
      { value: "+320%", label: "app installs" },
      { value: "4×", label: "influencer CVR vs brand creative" },
      { value: "+88%", label: "first-booking rate" },
      { value: "4.7★", label: "avg rating (was 4.1★)" },
    ],
    quote: {
      text: "We couldn't outspend category leaders on paid. AppFillip's influencer strategy got us to 320% install growth without touching our paid budget.",
      author: "Marketing Director, Travel App",
    },
  },

  {
    slug: "hotel-booking-app-aso-reviews",
    category: "Travel",
    appName: "Hotel Booking App",
    title: "Top 3 App Store rank for a hotel booking app globally",
    metric: "Top 3",
    metricLabel: "App Store category rank",
    summary:
      "ASO, Ratings & Reviews and Analytics took a hotel booking app to Top 3 App Store Travel category rank across 8 global markets — growing organic installs 4× in five months.",
    market: "Global · 8 markets",
    platform: "ios",
    services: ["ASO", "Ratings & Reviews", "Analytics"],
    duration: "5 months",
    challenge:
      "A hotel booking app was stuck on page 2–3 in every market. Ratings had slipped to 3.5★ after a product update, review velocity was low and metadata had not been touched in 18 months.",
    approach: [
      "Full metadata audit across 8 market App Store localisations — rewrote per-locale title, subtitle and keywords.",
      "Custom Product Pages per traffic source: organic search vs. brand paid vs. competitor ads — 3 variants per market.",
      "Review recovery programme: proactive outreach to 5★ historical users, contextual rating prompt post-booking.",
      "Monthly analytics review: impression-to-download funnel by market → iterative optimisation.",
    ],
    results: [
      { value: "Top 3", label: "App Store Travel rank (8 markets)" },
      { value: "4×", label: "organic installs" },
      { value: "4.6★", label: "avg rating (was 3.5★)" },
      { value: "+70%", label: "store conversion rate" },
    ],
    quote: {
      text: "Stuck on page 2 for 18 months. AppFillip's ASO and review programme took us to Top 3 in 8 markets in five months.",
      author: "App Marketing Lead, Hotel Booking App",
    },
  },

  // ─── MOBILITY & LOGISTICS ────────────────────────────────────────────────

  {
    slug: "ride-hailing-app-india-sea",
    category: "Mobility",
    appName: "Ride-Hailing App",
    title: "+235% Tier-1 and +145% Tier-2 install growth for a ride-hailing app",
    metric: "+235%",
    metricLabel: "install growth in Tier-1 cities",
    summary:
      "Paid Search, Paid Social and SEO drove +240% Tier-1 and +145% Tier-2 install growth for a ride-hailing app across India, Singapore and UAE with a city-tier campaign architecture.",
    market: "India · Singapore · UAE",
    platform: "both",
    services: ["Paid Search", "Paid Social", "SEO"],
    duration: "6 months",
    challenge:
      "A ride-hailing app needed to scale in India's competitive on-demand category. Install growth was capped by brand-search saturation in Tier-1 cities and weak awareness in Tier-2 and international markets.",
    approach: [
      "City-tier paid architecture — brand defence on Tier-1, volume push on Tier-2/3 India, premium intent on SG/UAE.",
      "Use-case Paid Social: 'ride to work', 'airport transfer', 'late-night ride' as discrete creative funnels per city.",
      "SEO targeting 'cheapest cab [city]', 'ride app near me' commercial queries.",
      "Install attribution tied to first-trip behaviour: quality scored, not just volume.",
    ],
    results: [
      { value: "+235%", label: "Tier-1 install growth" },
      { value: "+145%", label: "Tier-2/3 install growth" },
      { value: "+47%", label: "first-trip conversion" },
      { value: "−24%", label: "blended CAC" },
    ],
    quote: {
      text: "Tier-aware funnels were the unlock. AppFillip built them — Tier-1 up 240%, Tier-2 up 145%, and Tier-2 India is now 35% of our volume.",
      author: "Growth Lead, Ride-Hailing App",
    },
  },

  {
    slug: "logistics-app-india-expansion",
    category: "Mobility",
    appName: "Logistics App",
    title: "+190% Tier-1 and +112% Tier-2 install growth for a logistics app",
    metric: "+190%",
    metricLabel: "Tier-1 city install growth",
    summary:
      "Paid Search, Paid Social and SEO drove +190% installs in metros and +112% in secondary cities for an intra-city logistics app across Indian Tier-1 and Tier-2 markets.",
    market: "India · Multiple cities",
    platform: "both",
    services: ["Paid Search", "Paid Social", "SEO"],
    duration: "Ongoing retainer",
    challenge:
      "A logistics app had brand-search saturation in its home market and weak national awareness. Single-city campaigns weren't scalable and creative was too generic for diverse city audiences.",
    approach: [
      "City-tier campaign architecture: separate bid strategies, budgets and creative per tier.",
      "Use-case Paid Social: 'move home', 'send a parcel', 'business delivery' as separate creative funnels.",
      "SEO: 'cheapest moving service [city]', 'parcel delivery near me' commercial queries.",
      "Attribution setup: install → first-trip event tracking to score cohort quality.",
    ],
    results: [
      { value: "+190%", label: "Tier-1 city install growth" },
      { value: "+112%", label: "Tier-2/3 city install growth" },
      { value: "+47%", label: "first-trip conversion" },
      { value: "−24%", label: "blended CAC" },
    ],
    quote: {
      text: "City-level thinking is obvious in hindsight. AppFillip built it — metros up 190%, Tier-2 up 112%, and the install graph proves it.",
      author: "Growth Team, Logistics App",
    },
  },

  // ─── KIDS & FAMILY ───────────────────────────────────────────────────────

  {
    slug: "kids-education-app-organic",
    category: "Kids",
    appName: "Kids Education App",
    title: "+175% organic transactions for a kids education app",
    metric: "+175%",
    metricLabel: "organic in-app transactions",
    summary:
      "Organic Social, Paid Social and App Retention rebuilt the user journey and IAP monetisation for a children's education app — organic transactions grew 175% and D30 retention grew 38%.",
    market: "Global · iOS + Android",
    platform: "both",
    services: ["Organic Social Media", "Paid Social", "App Retention"],
    duration: "6 months",
    challenge:
      "A kids education app had massive install volume but organic transactions (IAP) were lagging. Acquisition was strong; monetisation discovery wasn't reaching parents effectively.",
    approach: [
      "Parent-led organic social: in-app learning moments, milestone tie-ins, IAP value props in authentic content.",
      "Paid Social retargeting to engaged parents with premium content bundle creative.",
      "Lifecycle messaging tied to child milestones: push and email when a child completes a free episode.",
      "Tested gentle IAP prompts parent-gate compliant — contextual trigger post-episode-completion.",
    ],
    results: [
      { value: "+175%", label: "organic in-app transactions" },
      { value: "+72%", label: "IAP revenue per active user" },
      { value: "+38%", label: "D30 retention" },
      { value: "Higher", label: "parent-rated NPS" },
    ],
    quote: {
      text: "Kids apps live and die on parent trust. AppFillip grew transactions 175% without ever feeling pushy — exactly the bar we needed.",
      author: "Growth Lead, Kids Education App",
    },
  },

  // ─── SOCIAL / ENTERTAINMENT ──────────────────────────────────────────────

  {
    slug: "dating-app-store-conversion",
    category: "Social",
    appName: "Dating App",
    title: "+96% store conversion for a dating app",
    metric: "+96%",
    metricLabel: "store conversion",
    summary:
      "A conversion-first store strategy - centered on audience positioning, trust signals, first-impression testing and benefit-led creative sequencing - helped a dating app achieve +96% store conversion while competing in the crowded US and UK dating categories.",
    market: "USA · UK",
    platform: "both",
    services: [
      "Conversion Optimization",
      "Creative Strategy",
      "ASO",
      "A/B Testing",
      "Analytics",
    ],
    duration: "105 days",
    challenge:
      "The dating app was already receiving meaningful store traffic, but too many potential users left the product page without installing. Its listing looked similar to dozens of competing dating apps: profile cards, chat screens and generic promises about meeting new people. The app's actual differentiators - compatibility-focused matching, verified profiles and safer conversations - weren't obvious during the few seconds users spent evaluating the listing. The challenge wasn't simply generating more impressions. It was giving existing visitors a stronger reason to choose this app over the alternatives beside it.",
    approach: [
      "Identified why users chose or rejected the app: competitor listings, review language and conversion behaviour were examined to understand the role of match quality, trust and meaningful conversations.",
      "Rewrote the first impression: the opening store message stopped describing what the app contained and started communicating why someone should care.",
      "Turned the screenshot gallery into a decision journey: the sequence moved from why the app is different, to how matching works, to profile trust, to post-match experience.",
      "Made trust visible before the install: profile verification, privacy controls and safety features were surfaced earlier in the gallery.",
      "Tested different reasons to install: creative variants explored serious compatibility, discovering similar people, verified users and easier conversations.",
      "Connected organic and paid store experiences: acquisition campaign messaging was aligned with the store page to create a consistent path from discovery to installation.",
    ],
    results: [
      { value: "+96%", label: "store conversion" },
      { value: "+71%", label: "screenshot engagement" },
      { value: "+58%", label: "installs from search" },
      { value: "-22%", label: "cost per acquired user" },
    ],
    quote: {
      text: "We kept thinking we needed more traffic. The bigger opportunity was converting the people already reaching our page. Once the listing communicated what made us different, the economics changed quickly.",
      author: "User Growth Lead, Dating App",
    },
  },

  {
    slug: "social-app-paid-social-retention",
    category: "Social",
    appName: "Social Entertainment App",
    title: "+50% engagement rate for a social entertainment app",
    metric: "+50%",
    metricLabel: "in-app engagement rate",
    summary:
      "Paid Social, Organic Social and App Retention rebuilt the engagement funnel for a social entertainment app — sessions, return visits and in-app actions all moved simultaneously across USA, Brazil and India.",
    market: "USA · Brazil · India",
    platform: "both",
    services: ["Paid Social", "Organic Social Media", "App Retention"],
    duration: "Ongoing retainer",
    challenge:
      "A social entertainment app had healthy install volume but engagement was plateauing. Sessions were short, return visits were inconsistent and the social presence was disconnected from the in-app experience.",
    approach: [
      "Rebuilt organic social calendar around trending content formats — daily content drops, user-generated challenges, event tie-ins.",
      "Paid Social retargeting: installers with no D3 session served a 'you're missing' re-engagement creative.",
      "Push notification programme: personalised to in-app behaviour (content category, creator followed).",
      "Tied creative themes across social and in-app so users saw a consistent narrative on both surfaces.",
    ],
    results: [
      { value: "+50%", label: "engagement rate" },
      { value: "+38%", label: "session frequency" },
      { value: "+29%", label: "D7 active users" },
      { value: "Higher", label: "creator content volume" },
    ],
    quote: {
      text: "AppFillip turned our social channels from a brand exercise into an engagement engine. Sessions, frequency and creator activity all moved at once.",
      author: "Growth Lead, Social Entertainment App",
    },
  },

  // ─── ANALYTICS / DATA ────────────────────────────────────────────────────

  {
    slug: "analytics-app-b2b-seo-paid",
    category: "Productivity",
    appName: "Analytics & Reporting App",
    title: "+96% B2B trial signups for an analytics app via paid + SEO",
    metric: "+96%",
    metricLabel: "B2B trial signups",
    summary:
      "Paid Search, SEO and GTM strategy nearly doubled qualified trial signups for a B2B analytics app across USA, UK and Germany — with a 45% reduction in cost-per-trial.",
    market: "USA · UK · Germany",
    platform: "both",
    services: ["Paid Search", "SEO", "GTM Strategy"],
    duration: "5 months",
    challenge:
      "A B2B analytics app had a strong product but poor organic discoverability and expensive Paid Search campaigns running broad keywords with low commercial intent. Trial sign-up quality was poor.",
    approach: [
      "Paid Search restructured: tight keyword grouping around 'analytics app for [role]', '[competitor] alternative' and pain-based queries.",
      "Negative keyword discipline removed research-intent and informational queries that diluted budget.",
      "SEO content: comparison pages ('best analytics app for [use case]'), ROI calculators, integration guides.",
      "German-language paid + SEO content stream built from scratch for DE market.",
    ],
    results: [
      { value: "+96%", label: "qualified trial signups" },
      { value: "−45%", label: "cost per trial" },
      { value: "+175%", label: "non-brand SEO traffic" },
      { value: "+38%", label: "trial-to-paid conversion" },
    ],
    quote: {
      text: "We were paying premium CPCs for unqualified traffic. AppFillip's restructure halved our cost-per-trial and doubled the quality of signups.",
      author: "Marketing Director, Analytics App",
    },
  },

];

export function getCaseStudyBySlug(slug) {
  return CASE_STUDIES_FULL.find((c) => c.slug === slug);
}
